
import React, { useState, useEffect } from 'react';
import { Page } from '@/types/page';
import { GeneralInfoSlide } from './slides/GeneralInfoSlide';
import { StudySlide } from './slides/StudySlide';
import { PrayerTimesSlide } from './slides/PrayerTimesSlide';
import { AnnouncementSlide } from './slides/AnnouncementSlide';
import { YahrzeitSlide } from './slides/YahrzeitSlide';
import { useHalachicDate } from '@/hooks/useHalachicDate';

interface UnifiedDisplayRendererProps {
  pages: Page[];
  rotationInterval?: number;
  autoRotate?: boolean;
}

interface SlideData {
  id: string;
  component: React.ReactNode;
  duration?: number; // Optional custom duration for this slide
}

export const UnifiedDisplayRenderer: React.FC<UnifiedDisplayRendererProps> = ({
  pages,
  rotationInterval = 8,
  autoRotate = true
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [fadeState, setFadeState] = useState<'fade-in' | 'fade-out' | ''>('fade-in');
  const { hebrewDate, gregorianDate } = useHalachicDate();
  
  // Generate slides from pages
  const generateSlides = (): SlideData[] => {
    const slides: SlideData[] = [];

    // Always start with general info slide
    slides.push({
      id: 'general-info',
      component: (
        <GeneralInfoSlide
          date={gregorianDate}
          hebrewDate={hebrewDate}
          parsha="פרשת השבוע" // This should come from actual data
          omer="יום טוב" // This should come from actual data
        />
      ),
      duration: 6
    });

    // Process each page type
    pages.forEach((page) => {
      if (!page.enabled) return;

      switch (page.type) {
        case 'daily-study':
          if (page.content?.items) {
            // Split study items into multiple slides if needed
            const items = page.content.items;
            const slidesNeeded = Math.ceil(items.length / 4);
            
            for (let i = 0; i < slidesNeeded; i++) {
              const slideItems = items.slice(i * 4, (i + 1) * 4);
              slides.push({
                id: `study-${i}`,
                component: (
                  <StudySlide
                    items={slideItems}
                    title={i === 0 ? "לימוד יומי" : `לימוד יומי (${i + 1})`}
                  />
                ),
                duration: page.useCustomRotationTime ? (page.rotationTimeMs || 8) : 8
              });
            }
          }
          break;

        case 'prayer-times':
          if (page.content?.sections || page.content?.prayerTimes) {
            const times = page.content.sections?.[0]?.prayerTimes || page.content.prayerTimes || [];
            slides.push({
              id: 'prayer-times',
              component: (
                <PrayerTimesSlide
                  times={times}
                  title="זמני תפילה"
                />
              ),
              duration: page.useCustomRotationTime ? (page.rotationTimeMs || 8) : 8
            });
          }
          break;

        case 'yahrzeit_display':
          if (page.content?.mockData) {
            slides.push({
              id: 'yahrzeit',
              component: (
                <YahrzeitSlide
                  entries={page.content.mockData}
                  title="לעילוי נשמת"
                />
              ),
              duration: page.useCustomRotationTime ? (page.rotationTimeMs || 8) : 8
            });
          }
          break;

        case 'custom':
          // Handle custom content as announcements
          if (page.content?.text) {
            slides.push({
              id: `custom-${page.id}`,
              component: (
                <AnnouncementSlide
                  announcements={[{
                    title: page.title,
                    content: page.content.text,
                    priority: 'medium'
                  }]}
                />
              ),
              duration: page.useCustomRotationTime ? (page.rotationTimeMs || 8) : 8
            });
          }
          break;

        // Add more page types as needed
        default:
          console.log(`Unsupported page type for unified display: ${page.type}`);
      }
    });

    return slides;
  };

  const slides = generateSlides();

  // Auto-rotation logic
  useEffect(() => {
    if (!autoRotate || slides.length <= 1) return;

    const currentSlide = slides[currentSlideIndex];
    const duration = (currentSlide?.duration || rotationInterval) * 1000;

    const timer = setTimeout(() => {
      setFadeState('fade-out');
      
      setTimeout(() => {
        setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
        setFadeState('fade-in');
      }, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [currentSlideIndex, slides, rotationInterval, autoRotate]);

  // Handle empty slides
  if (slides.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <GeneralInfoSlide
          date={gregorianDate}
          hebrewDate={hebrewDate}
          specialDay="אין תוכן להצגה"
        />
      </div>
    );
  }

  const currentSlide = slides[currentSlideIndex];

  return (
    <div className={`w-full h-full transition-opacity duration-300 ${fadeState === 'fade-out' ? 'opacity-0' : 'opacity-100'}`}>
      {currentSlide?.component}
    </div>
  );
};
